import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { licenseAPI } from '@/services/api';
import type { LicenseKey } from '../types';

interface UseLicensesReturn {
  licenses: LicenseKey[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  create: (payload: { key: string; max_activations: number; expires_at?: string; notes?: string }) => Promise<string>;
  update: (id: number, payload: { max_activations: number; expires_at: string | null; notes: string | null; is_active: boolean }) => Promise<void>;
  batchCreate: (payload: { count: number; max_activations?: number; expires_at?: string; notes?: string; prefix?: string; template?: string }) => Promise<LicenseKey[]>;
  batchDeactivate: (ids: number[]) => Promise<void>;
  toggleActivation: (id: number, current: boolean) => Promise<void>;
}

/**
 * 许可证数据 CRUD 操作 Hook
 */
export function useLicenses(): UseLicensesReturn {
  const { t } = useTranslation();

  const [licenses, setLicenses] = useState<LicenseKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await licenseAPI.getAll();
      setLicenses(response.data || []);
    } catch (requestError: any) {
      console.error('Failed to load licenses:', requestError);
      if (requestError.response?.status === 401) {
        setError(t('licenses.sessionExpired'));
      } else {
        setError(t('licenses.loadFailed'));
      }
      setLicenses([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  const create = useCallback(async (payload: {
    key: string;
    max_activations: number;
    expires_at?: string;
    notes?: string;
  }): Promise<string> => {
    const response = await licenseAPI.create(payload);
    return response.data.key || payload.key;
  }, []);

  const update = useCallback(async (
    id: number,
    payload: {
      max_activations: number;
      expires_at: string | null;
      notes: string | null;
      is_active: boolean;
    }
  ): Promise<void> => {
    await licenseAPI.update(id, payload);
  }, []);

  const batchCreate = useCallback(async (payload: {
    count: number;
    max_activations?: number;
    expires_at?: string;
    notes?: string;
    prefix?: string;
    template?: string;
  }): Promise<LicenseKey[]> => {
    const response = await licenseAPI.batchCreate(payload);
    return response.data || [];
  }, []);

  const batchDeactivate = useCallback(async (ids: number[]): Promise<void> => {
    await licenseAPI.batchDeactivate(ids);
  }, []);

  const toggleActivation = useCallback(async (id: number, currentState: boolean): Promise<void> => {
    await licenseAPI.update(id, { is_active: !currentState });
  }, []);

  return {
    licenses,
    loading,
    error,
    refresh,
    create,
    update,
    batchCreate,
    batchDeactivate,
    toggleActivation,
  };
}
